import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  trackActivitiesError,
  trackActivitiesEvent,
} from "../analytics/activities";
import { useCreateActivityModelMutation } from "./useCreateActivityModelMutation";
import { useCreateActivityMutation } from "./useCreateActivityMutation";
import { useUpdateActivityModelMutation } from "./useUpdateActivityModelMutation";
import { useUpdateActivityMutation } from "./useUpdateActivityMutation";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import type {
  ActivityCreatePayload,
  ActivityModelCreatePayload,
  ActivityModelUpdatePayload,
  ActivityUpdatePayload,
} from "../repositories/activitiesRepository";
import type { FormChatState } from "../repositories/formChat.repository";
import { buildActivityFormChatPayload } from "../utils/activityFormChatPayloadBuilder";
import type { ActivityType } from "../types/activity.types";
import { getActivityRoutes } from "../utils/activityTypeRoutes";
import { resolveCreatedFromModelPath } from "../utils/createdFromModelPath";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { buildPathWithStatusTabSearch } from "../utils/statusTabNavigation";
import { toast } from "../utils/toast";

const activityCopy = (activityType?: ActivityType) =>
  activityType === "RITUAL"
    ? {
        created: "Ritual criado.",
        updated: "Ritual atualizado.",
        createError: "Não foi possível criar o ritual.",
        updateError: "Não foi possível salvar o ritual.",
      }
    : {
        created: "Atividade criada.",
        updated: "Atividade atualizada.",
        createError: "Não foi possível criar a atividade.",
        updateError: "Não foi possível salvar a atividade.",
      };

export const activityWriteSuccessMessage = (
  activityType: ActivityType | undefined,
  isTemplate: boolean,
  action: "create" | "update",
) => {
  if (isTemplate) {
    return action === "create" ? "Modelo criado." : "Modelo atualizado.";
  }
  const copy = activityCopy(activityType);
  return action === "create" ? copy.created : copy.updated;
};

export const activityWriteErrorMessage = (
  error: unknown,
  activityType: ActivityType | undefined,
  isTemplate: boolean,
  action: "create" | "update",
) => {
  const fallback = isTemplate
    ? "Não foi possível salvar o modelo."
    : action === "create"
      ? activityCopy(activityType).createError
      : activityCopy(activityType).updateError;
  return extractAxiosApiError(error, fallback).message;
};

interface SubmitActivityByChatInput {
  data: FormChatState;
  modelId?: string;
  activityType?: ActivityType;
}

interface UpdateActivityByChatInput {
  data: FormChatState;
  activityId: string;
  activityType?: ActivityType;
}

export function useFormChatSubmitActivity() {
  const navigate = useNavigate();
  const location = useLocation();
  const productBase = useProductAnalyticsBase();
  const { mutateAsync: createActivity } = useCreateActivityMutation();
  const { mutateAsync: createActivityModel } = useCreateActivityModelMutation();
  const { mutateAsync: updateActivity } = useUpdateActivityMutation();
  const { mutateAsync: updateActivityModel } = useUpdateActivityModelMutation();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};

  const submitActivityByChat = useCallback(
    async ({ data, modelId, activityType }: SubmitActivityByChatInput) => {
      const scopedData = modelId ? { ...data, isTemplate: false } : data;
      const payload = buildActivityFormChatPayload({
        data: scopedData,
        terreiroId,
        mediumId,
        activityType,
      });

      if (!payload) {
        throw new Error("No owner scope found for activity creation.");
      }

      const isTemplate = Boolean(scopedData.isTemplate);

      trackActivitiesEvent(
        "activity_form_chat_submitted",
        { ...productBase, trigger: "mutation" },
        { is_template: isTemplate },
      );
      if (modelId) {
        trackActivitiesEvent(
          "activity_model_create_submit_attempt",
          { ...productBase, trigger: "mutation" },
          { model_id: modelId, source: "form_chat" },
        );
      }

      try {
        const created: unknown = isTemplate
          ? await createActivityModel(payload as ActivityModelCreatePayload)
          : await createActivity(payload as ActivityCreatePayload);

        trackActivitiesEvent(
          modelId ? "activity_model_created" : "activity_created",
          { ...productBase, trigger: "mutation" },
          modelId
            ? { model_id: modelId, source: "form_chat" }
            : { is_template: isTemplate, source: "form_chat" },
        );

        toast.success(
          activityWriteSuccessMessage(activityType, isTemplate, "create"),
        );
        navigate(
          modelId
            ? resolveCreatedFromModelPath(
                created,
                activityType,
                location.search,
              )
            : buildPathWithStatusTabSearch(
                getActivityRoutes(activityType).list,
                location.search,
                {
                  rebalance: true,
                },
              ),
        );
      } catch (error) {
        trackActivitiesError(
          "activity_form_chat_submit_error",
          { ...productBase, trigger: "mutation" },
          error,
          { is_template: isTemplate },
        );
        toast.error(
          activityWriteErrorMessage(error, activityType, isTemplate, "create"),
        );
        throw error;
      }
    },
    [
      createActivity,
      createActivityModel,
      location.search,
      mediumId,
      navigate,
      productBase,
      terreiroId,
    ],
  );

  const updateActivityByChat = useCallback(
    async ({ activityId, data, activityType }: UpdateActivityByChatInput) => {
      const payload = buildActivityFormChatPayload({
        data,
        terreiroId,
        mediumId,
        activityType,
      });

      if (!payload) {
        throw new Error("No owner scope found for activity update.");
      }

      const isTemplate = Boolean(data.isTemplate);

      trackActivitiesEvent(
        "activity_update_submit_attempt",
        { ...productBase, trigger: "mutation" },
        { activity_id: activityId, is_template: isTemplate, source: "form_chat" },
      );

      try {
        if (isTemplate) {
          await updateActivityModel({
            ...payload,
            id: activityId,
          } as ActivityModelUpdatePayload);
        } else {
          await updateActivity({ ...payload, id: activityId } as ActivityUpdatePayload);
        }

        trackActivitiesEvent(
          "activity_updated",
          { ...productBase, trigger: "mutation" },
          { activity_id: activityId, is_template: isTemplate, source: "form_chat" },
        );

        toast.success(
          activityWriteSuccessMessage(activityType, isTemplate, "update"),
        );
        navigate(
          buildPathWithStatusTabSearch(
            getActivityRoutes(activityType).detail(activityId),
            location.search,
            {
              rebalance: false,
            },
          ),
          { state: location.state },
        );
      } catch (error) {
        trackActivitiesError(
          "activity_update_error",
          { ...productBase, trigger: "mutation" },
          error,
          { activity_id: activityId, is_template: isTemplate, source: "form_chat" },
        );
        toast.error(
          activityWriteErrorMessage(error, activityType, isTemplate, "update"),
        );
        throw error;
      }
    },
    [
      location.search,
      location.state,
      mediumId,
      navigate,
      productBase,
      terreiroId,
      updateActivity,
      updateActivityModel,
    ],
  );

  return { submitActivityByChat, updateActivityByChat };
}
